import React from 'react';
import ScrollX from './ScrollX';

// Dependency-free charts for the Expense Dashboard, matching this
// codebase's existing convention (see PaymentsBarChart.js) of plain
// CSS/SVG rather than pulling in a charting library - recharts is listed
// in package.json but isn't actually used anywhere yet, and this sandbox
// has no `npm install` to verify it resolves (see CLAUDE.md section 6).

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Indian-style compact amounts for crowded charts: ₹1.2Cr / ₹4.5L / ₹45K.
const moneyCompact = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1).replace(/\.0$/, '')}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return `₹${Math.round(v)}`;
};

const PALETTE = ['#2F6690', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'];

// True horizontal bars - label left, bar grows rightward. Used for
// "horizontal bar" chart slots (e.g. by-vendor). onBarClick, when passed,
// makes each bar a drill-down into the underlying transactions.
const HorizontalBars = ({ data, labelKey, valueKey, max, onBarClick }) => (
  <div className="space-y-2.5">
    {data.map((d, i) => (
      <div
        key={d[labelKey] || i}
        onClick={onBarClick ? () => onBarClick(d) : undefined}
        className={onBarClick ? 'cursor-pointer' : undefined}
      >
        <div className="flex justify-between text-xs mb-1">
          <span className={`text-gray-700 truncate max-w-[60%] ${onBarClick ? 'hover:underline' : ''}`}>{d[labelKey]}</span>
          <span className="font-medium">{money(d[valueKey])}</span>
        </div>
        <div className="bg-gray-100 rounded h-2.5">
          <div
            className="h-2.5 rounded"
            style={{ width: `${Math.max((d[valueKey] / max) * 100, 2)}%`, backgroundColor: PALETTE[i % PALETTE.length] }}
          />
        </div>
      </div>
    ))}
  </div>
);

// True vertical columns - bars grow upward from a shared baseline, labels
// below. Used for "bar" chart slots (e.g. by-project) so it reads visibly
// differently from the horizontal-bar chart type, per the PRD's 6 distinct
// chart types.
const VerticalBars = ({ data, labelKey, valueKey, max, onBarClick }) => (
  <div className="flex items-end gap-3 h-48 border-b border-gray-100 px-1">
    {data.map((d, i) => (
      <div
        key={d[labelKey] || i}
        onClick={onBarClick ? () => onBarClick(d) : undefined}
        className={`flex-1 flex flex-col items-center justify-end h-full min-w-0 ${onBarClick ? 'cursor-pointer group' : ''}`}
      >
        <span className="text-[11px] font-medium text-gray-700 mb-1">{money(d[valueKey])}</span>
        <div
          className={`w-full rounded-t ${onBarClick ? 'group-hover:opacity-75' : ''}`}
          style={{ height: `${Math.max((d[valueKey] / max) * 100, 2)}%`, backgroundColor: PALETTE[i % PALETTE.length] }}
        />
        <span className={`text-[11px] text-gray-500 mt-1.5 truncate max-w-full ${onBarClick ? 'group-hover:underline' : ''}`}>{d[labelKey]}</span>
      </div>
    ))}
  </div>
);

// Stacked vertical columns - each bar is split into named segments
// (site/vendor/material, etc.) with the bar's total shown above it and
// each segment's own amount as a hover tooltip (2026-09-08: "show total
// project cost by project, split by bucket" + "amounts on the graph").
// segments: [{ key, label, color }]
export const StackedBarChart = ({ title, data, labelKey, segments, onBarClick }) => {
  const totals = data.map((d) => segments.reduce((sum, s) => sum + Number(d[s.key] || 0), 0));
  const max = Math.max(1, ...totals);
  const many = data.length > 8;
  return (
    <div>
      {title && <div className="text-xs text-gray-500 mb-3">{title}</div>}
      {data.length === 0 ? (
        <p className="text-xs text-gray-400">No data in this range.</p>
      ) : (
        <>
          <div className="flex gap-4 mb-3">
            {segments.map((s) => (
              <div key={s.key} className="flex items-center gap-1.5 text-xs text-gray-600">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
                {s.label}
              </div>
            ))}
          </div>
          {/* Many bars (the dashboard shows the top 20 projects): compact
              amounts (₹4.5L) and vertical name labels, since full amounts
              and horizontal names collide once columns are ~50px wide. The
              bar area is its own flex-1 region so the tallest bar can no
              longer overflow above the amount label. */}
          <div className={`flex items-stretch ${many ? 'gap-1.5 h-80' : 'gap-3 h-48'} border-b border-gray-100 px-1`}>
            {data.map((d, i) => (
              <div
                key={d[labelKey] || i}
                onClick={onBarClick ? () => onBarClick(d) : undefined}
                title={`${d[labelKey]}: ${money(totals[i])}`}
                className={`flex-1 flex flex-col items-center h-full min-w-0 ${onBarClick ? 'cursor-pointer group' : ''}`}
              >
                <span className="text-[11px] font-medium text-gray-700 mb-1 whitespace-nowrap">{many ? moneyCompact(totals[i]) : money(totals[i])}</span>
                <div className="w-full flex-1 min-h-0 flex flex-col justify-end">
                  <div className="w-full flex flex-col justify-end" style={{ height: `${Math.max((totals[i] / max) * 100, 2)}%` }}>
                    {segments.filter((s) => Number(d[s.key] || 0) > 0).map((s) => (
                      <div
                        key={s.key}
                        title={`${s.label}: ${money(d[s.key])}`}
                        className={`w-full first:rounded-t ${onBarClick ? 'group-hover:opacity-75' : ''}`}
                        style={{
                          height: `${(Number(d[s.key] || 0) / totals[i]) * 100}%`,
                          backgroundColor: s.color,
                          borderTop: '2px solid white',
                        }}
                      />
                    ))}
                  </div>
                </div>
                {many ? (
                  <span
                    className={`text-[11px] text-gray-500 mt-1.5 h-24 overflow-hidden text-ellipsis whitespace-nowrap ${onBarClick ? 'group-hover:underline' : ''}`}
                    style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                  >
                    {d[labelKey]}
                  </span>
                ) : (
                  <span className={`text-[11px] text-gray-500 mt-1.5 truncate max-w-full ${onBarClick ? 'group-hover:underline' : ''}`}>{d[labelKey]}</span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const BarChart = ({ title, data, labelKey, valueKey, horizontal = false, onBarClick }) => {
  const max = Math.max(1, ...data.map((d) => d[valueKey]));
  return (
    <div>
      {title && <div className="text-xs text-gray-500 mb-3">{title}</div>}
      {data.length === 0 ? (
        <p className="text-xs text-gray-400">No data in this range.</p>
      ) : horizontal ? (
        <HorizontalBars data={data} labelKey={labelKey} valueKey={valueKey} max={max} onBarClick={onBarClick} />
      ) : (
        <VerticalBars data={data} labelKey={labelKey} valueKey={valueKey} max={max} onBarClick={onBarClick} />
      )}
    </div>
  );
};

export const PieChart = ({ title, data, labelKey, valueKey }) => {
  const total = data.reduce((s, d) => s + Number(d[valueKey] || 0), 0);
  let cumulative = 0;
  const gradientStops = data.map((d, i) => {
    const pct = total > 0 ? (d[valueKey] / total) * 100 : 0;
    const start = cumulative;
    cumulative += pct;
    return `${PALETTE[i % PALETTE.length]} ${start}% ${cumulative}%`;
  });

  return (
    <div>
      {title && <div className="text-xs text-gray-500 mb-3">{title}</div>}
      {data.length === 0 || total === 0 ? (
        <p className="text-xs text-gray-400">No data in this range.</p>
      ) : (
        <div className="flex items-center gap-6">
          <div
            className="w-28 h-28 rounded-full flex-shrink-0"
            style={{ background: `conic-gradient(${gradientStops.join(', ')})` }}
          />
          <div className="space-y-1.5 min-w-0">
            {data.map((d, i) => (
              <div key={d[labelKey] || i} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
                <span className="text-gray-700 truncate">{d[labelKey]}</span>
                <span className="text-gray-400">{total > 0 ? ((d[valueKey] / total) * 100).toFixed(0) : 0}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const LineChart = ({ title, data, xKey, series }) => {
  // series: [{ key, color, label }]
  const width = 560;
  const height = 160;
  const padding = 24;
  const maxVal = Math.max(1, ...data.flatMap((d) => series.map((s) => Number(d[s.key] || 0))));

  const pointsFor = (key) =>
    data
      .map((d, i) => {
        const x = padding + (i / Math.max(data.length - 1, 1)) * (width - 2 * padding);
        const y = height - padding - (Number(d[key] || 0) / maxVal) * (height - 2 * padding);
        return `${x},${y}`;
      })
      .join(' ');

  return (
    <div>
      {title && <div className="text-xs text-gray-500 mb-3">{title}</div>}
      {data.length === 0 ? (
        <p className="text-xs text-gray-400">No data in this range.</p>
      ) : (
        <>
          <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-40">
            <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#E5E7EB" strokeWidth="1" />
            {series.map((s) => (
              <polyline key={s.key} points={pointsFor(s.key)} fill="none" stroke={s.color} strokeWidth="2" />
            ))}
          </svg>
          <div className="flex gap-4 mt-2">
            {series.map((s) => (
              <div key={s.key} className="flex items-center gap-1.5 text-xs text-gray-600">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
                {s.label}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const Heatmap = ({ title, data, rowKey, colKey, valueKey }) => {
  const rows = [...new Set(data.map((d) => d[rowKey]))];
  const cols = [...new Set(data.map((d) => d[colKey]))];
  const max = Math.max(1, ...data.map((d) => d[valueKey]));
  const valueAt = (r, c) => data.find((d) => d[rowKey] === r && d[colKey] === c)?.[valueKey] || 0;

  return (
    <div>
      {title && <div className="text-xs text-gray-500 mb-3">{title}</div>}
      {rows.length === 0 || cols.length === 0 ? (
        <p className="text-xs text-gray-400">No data in this range.</p>
      ) : (
        <ScrollX>
          <table className="text-xs border-collapse">
            <thead>
              <tr>
                <th className="p-1 text-left text-gray-400 font-normal"></th>
                {cols.map((c) => <th key={c} className="p-1 text-gray-500 font-medium">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r}>
                  <td className="p-1 text-gray-600 font-medium whitespace-nowrap">{r}</td>
                  {cols.map((c) => {
                    const v = valueAt(r, c);
                    const intensity = v / max;
                    return (
                      <td key={c} className="p-1 text-center" title={money(v)}>
                        <div
                          className="w-12 h-8 rounded flex items-center justify-center text-[10px]"
                          style={{
                            backgroundColor: `rgba(47, 102, 144, ${Math.max(intensity, v > 0 ? 0.12 : 0.03)})`,
                            color: intensity > 0.5 ? '#fff' : '#374151',
                          }}
                        >
                          {v > 0 ? money(v).replace('₹', '') : ''}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      )}
    </div>
  );
};

export const ExpenseList = ({ title, items }) => (
  <div>
    {title && <div className="text-xs text-gray-500 mb-3">{title}</div>}
    {items.length === 0 ? (
      <p className="text-xs text-gray-400">No data in this range.</p>
    ) : (
      <div className="space-y-1">
        {items.map((item, i) => (
          <div key={item.id || i} className="flex justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
            <span className="text-gray-700 truncate max-w-[70%]">{item.description || item.type || `Expense #${item.id}`}</span>
            <span className="font-medium">{money(item.amount)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);
